import TaggingPlanEventApiClient from "~/apiClients/TaggingPlanEventApiClient";
import DateHelper from "~/helpers/DateHelper";

export interface TaggingPlanEventAttributes {
  id: number,
  taggingPlanId: number,
  name: string,
  type: number,
  expectedValues: object,
  contexts: object,
  description: string,
  scenario: string,
  trigger: string,
  notes?: string,
  isDeleted: boolean,
  createdAt: string,
  updatedAt?: string,
  updatedBy?: string,
}

export interface TaggingPlanEventCreationAttributes {
  taggingPlanId: number,
  name: string,
  type: number,
  expectedValues: object,
  contexts: object,
  description: string,
  scenario: string,
  trigger: string,
  notes?: string,
}

export interface TaggingPlanEventUpdateAttributes {
  name?: string,
  type?: number,
  expectedValues?: object,
  contexts?: object,
  description?: string,
  scenario?: string,
  trigger?: string,
  notes?: string,
}

export class TaggingPlanEvent {
  constructor(private readonly attributes: TaggingPlanEventAttributes) {
  }

  get id(): number {
    return this.attributes.id;
  }

  get taggingPlanId(): number {
    return this.attributes.taggingPlanId;
  }

  get name(): string {
    return this.attributes.name;
  }

  get typeId(): number {
    return this.attributes.type;
  }

  get typeLabel(): string | undefined {
    return TaggingPlanEvent.getTypeById(this.typeId)?.label;
  }

  get expectedValues(): object {
    return this.attributes.expectedValues;
  }

  get contexts(): object {
    return this.attributes.contexts;
  }

  get description(): string {
    return this.attributes.description;
  }

  get scenario(): string {
    return this.attributes.scenario;
  }

  get trigger(): string {
    return this.attributes.trigger;
  }

  get notes(): string | undefined {
    return this.attributes.notes;
  }

  get lastModified(): string {
    return DateHelper.format(this.attributes.updatedAt ?? this.attributes.createdAt);
  }

  static getTypes() {
    return [
      { id: 1, label: 'Structured' },
      { id: 2, label: 'Unstructured' },
      { id: 3, label: 'PageView' },
      { id: 4, label: 'LinkClick' },
      { id: 5, label: 'AdClick' },
      { id: 6, label: 'AdImpression' },
      { id: 7, label: 'Error' },
    ];
  }

  static getTypeById(id: number) {
    return TaggingPlanEvent.getTypes().find((type) => type.id === id);
  }

  static async findAll(taggingPlanId: number) {
    return await this.getApiClient().provideList(taggingPlanId);
  }

  static async findById(id: number) {
    return await this.getApiClient().provideById(id);
  }

  static async create(attributes: TaggingPlanEventCreationAttributes) {
    return await this.getApiClient().create(attributes);
  }

  async update(attributes: TaggingPlanEventUpdateAttributes): Promise<TaggingPlanEvent> {
    return await TaggingPlanEvent.getApiClient().update({ ...attributes, id: this.id });
  }

  static async delete(id: number) {
    return await this.getApiClient().delete(id);
  }

  private static getApiClient(): TaggingPlanEventApiClient {
    return new TaggingPlanEventApiClient();
  }
}
